const CART_BODY_SELECTOR = ".sale-cart-body";
const CART_LINE_SELECTOR = ".sale-cart-line-shell";
const CART_VIEWPORT_GUTTER_PX = 6;
const CART_FEEDBACK_ATTRIBUTE = "data-cart-recent";

let activeCartLine: HTMLElement | null = null;

function cartLineFromNode(node: Node) {
  const element = node instanceof HTMLElement ? node : node.parentElement;
  return element?.closest<HTMLElement>(CART_LINE_SELECTOR) ?? null;
}

function addedCartLine(mutation: MutationRecord) {
  let target: HTMLElement | null = null;

  for (const node of mutation.addedNodes) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.matches(CART_LINE_SELECTOR)) {
      target = node;
      continue;
    }
    const nested = node.querySelectorAll<HTMLElement>(CART_LINE_SELECTOR);
    if (nested.length) target = nested.item(nested.length - 1);
  }

  return target;
}

function mutationCartLine(mutation: MutationRecord) {
  if (mutation.type === "characterData") return cartLineFromNode(mutation.target);

  const added = addedCartLine(mutation);
  if (added) return added;

  return cartLineFromNode(mutation.target);
}

function focusCartLine(cartLine: HTMLElement) {
  if (activeCartLine === cartLine) {
    cartLine.setAttribute(CART_FEEDBACK_ATTRIBUTE, "true");
    return;
  }

  if (activeCartLine?.isConnected) {
    activeCartLine.removeAttribute(CART_FEEDBACK_ATTRIBUTE);
  }

  activeCartLine = cartLine;
  cartLine.setAttribute(CART_FEEDBACK_ATTRIBUTE, "true");
}

function ensureCartLineVisible(cartBody: HTMLElement, cartLine: HTMLElement) {
  if (!cartBody.contains(cartLine) || !cartBody.isConnected || !cartLine.isConnected) return;

  const bodyRect = cartBody.getBoundingClientRect();
  const lineRect = cartLine.getBoundingClientRect();
  const visibleTop = bodyRect.top + CART_VIEWPORT_GUTTER_PX;
  const visibleBottom = bodyRect.bottom - CART_VIEWPORT_GUTTER_PX;

  if (lineRect.top >= visibleTop && lineRect.bottom <= visibleBottom) return;

  let nextTop = cartBody.scrollTop;
  if (lineRect.top < visibleTop) {
    nextTop -= visibleTop - lineRect.top;
  } else if (lineRect.bottom > visibleBottom) {
    nextTop += lineRect.bottom - visibleBottom;
  }

  const maxTop = Math.max(0, cartBody.scrollHeight - cartBody.clientHeight);
  const clampedTop = Math.min(maxTop, Math.max(0, nextTop));
  if (Math.abs(clampedTop - cartBody.scrollTop) < 1) return;
  cartBody.scrollTop = clampedTop;
}

/**
 * WebPOS UX adapter: whenever React changes a cart line (new line or quantity
 * update), keep that affected line inside the bounded cart viewport and keep it
 * highlighted until another cart line becomes the latest affected line.
 *
 * Cart mutations can arrive in several MutationObserver batches during one React
 * interaction (optimistic state + durable local persistence). Highlight feedback
 * is immediate, while scroll positioning is coalesced to the latest affected line
 * and written at most once per animation frame. Browser scroll anchoring is also
 * disabled on the cart body by CartSwipe.css, leaving one scroll authority.
 */
export function registerCartMutationVisibility() {
  if (typeof document === "undefined" || !document.body || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  let observedCartBody: HTMLElement | null = null;
  let cartObserver: MutationObserver | null = null;
  let pendingCartLine: HTMLElement | null = null;
  let visibilityFrame: number | null = null;

  const cancelPendingVisibility = () => {
    pendingCartLine = null;
    if (visibilityFrame !== null) {
      cancelAnimationFrame(visibilityFrame);
      visibilityFrame = null;
    }
  };

  const scheduleVisibility = (cartBody: HTMLElement, cartLine: HTMLElement) => {
    focusCartLine(cartLine);
    pendingCartLine = cartLine;
    if (visibilityFrame !== null) return;

    visibilityFrame = requestAnimationFrame(() => {
      visibilityFrame = null;
      const latestLine = pendingCartLine;
      pendingCartLine = null;
      if (latestLine) ensureCartLineVisible(cartBody, latestLine);
    });
  };

  const attachCartObserver = () => {
    if (observedCartBody?.isConnected) return;

    const nextCartBody = document.querySelector<HTMLElement>(CART_BODY_SELECTOR);
    if (nextCartBody === observedCartBody) return;

    cartObserver?.disconnect();
    cartObserver = null;
    cancelPendingVisibility();
    observedCartBody = nextCartBody;

    if (activeCartLine && !activeCartLine.isConnected) activeCartLine = null;
    if (!nextCartBody) return;

    cartObserver = new MutationObserver((mutations) => {
      let affectedLine: HTMLElement | null = null;
      for (const mutation of mutations) {
        const candidate = mutationCartLine(mutation);
        if (candidate) affectedLine = candidate;
      }
      if (affectedLine) scheduleVisibility(nextCartBody, affectedLine);
    });

    cartObserver.observe(nextCartBody, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  const workspaceObserver = new MutationObserver(attachCartObserver);
  workspaceObserver.observe(document.body, { childList: true, subtree: true });
  attachCartObserver();

  return () => {
    workspaceObserver.disconnect();
    cartObserver?.disconnect();
    cancelPendingVisibility();
    if (activeCartLine?.isConnected) activeCartLine.removeAttribute(CART_FEEDBACK_ATTRIBUTE);
    activeCartLine = null;
    observedCartBody = null;
  };
}
